import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@/analytics/events";
import { Button } from "@/components/ui/Button";
import { InputField } from "@/components/ui/InputField";
import { Sheet } from "@/components/ui/Sheet";
import { useCreateTaskMutation } from "@/hooks/useBackendData";
import { useAppStore } from "@/state/appStore";

interface Props {
  open: boolean;
  onClose: () => void;
}

export const AddTaskSheet = ({ open, onClose }: Props): JSX.Element | null => {
  const projects = useAppStore((state) => state.projects);
  const agents = useAppStore((state) => state.agents);
  const members = useAppStore((state) => state.members);
  const activeProjectId = useAppStore((state) => state.activeProjectId);
  const setActiveTask = useAppStore((state) => state.setActiveTask);
  const navigate = useNavigate();
  const createTaskMutation = useCreateTaskMutation();
  const connectedAgents = useMemo(() => agents.filter((agent) => agent.status === "active"), [agents]);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [definitionOfDone, setDefinitionOfDone] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [projectId, setProjectId] = useState(activeProjectId);
  const [dueAt, setDueAt] = useState("");
  const [attachmentNames, setAttachmentNames] = useState<string[]>([]);
  const [assigneeSelection, setAssigneeSelection] = useState("auto");
  const [autoApproveRemoteAgentActions, setAutoApproveRemoteAgentActions] = useState(false);

  const assignee = useMemo(() => {
    if (assigneeSelection === "auto") return { mode: "auto" as const, id: undefined };
    const [kind, id] = assigneeSelection.split(":", 2);
    if (kind === "agent") return { mode: "agent" as const, id };
    if (kind === "member") return { mode: "member" as const, id };
    return { mode: "auto" as const, id: undefined };
  }, [assigneeSelection]);

  const clarityPct = useMemo(() => {
    const words = title.trim().split(/\s+/).filter(Boolean).length;
    const detail = `${description.trim()} ${definitionOfDone.trim()}`.trim().length;
    return Math.min(100, Math.round(words * 8 + detail / 8));
  }, [title, description, definitionOfDone]);

  const safeProjectId = useMemo(() => {
    if (projects.some((project) => project.id === projectId)) return projectId;
    if (projects.some((project) => project.id === activeProjectId)) return activeProjectId;
    return projects[0]?.id ?? "proj_default";
  }, [activeProjectId, projectId, projects]);

  useEffect(() => {
    if (!open) return;
    trackEvent("add_task_sheet_opened", {});
    setProjectId((current) => {
      if (projects.some((project) => project.id === current)) return current;
      if (projects.some((project) => project.id === activeProjectId)) return activeProjectId;
      return projects[0]?.id ?? "proj_default";
    });
  }, [activeProjectId, open, projects]);

  if (!open) return null;

  return (
    <Sheet open={open} title="Add Task" onClose={onClose} height="80">
      <div className="mb-4 h-1 rounded-full" style={{ background: `linear-gradient(90deg, var(--status-blocker) 0%, var(--status-warning) 50%, var(--status-success) ${clarityPct}%)` }} />
      <div className="space-y-3">
        <InputField
          label="Title *"
          inputProps={{ value: title, onChange: (event) => setTitle(event.target.value), placeholder: "Build login page" }}
        />

        <button type="button" className="btn btn-ghost !min-h-0 !p-0 text-sm" onClick={() => setShowDetails((prev) => !prev)}>
          {showDetails ? "Hide details" : "Add details"}
        </button>

        {showDetails ? (
          <div className="card space-y-3 p-3">
            <InputField
              kind="textarea"
              label="Description"
              textareaProps={{ value: description, onChange: (event) => setDescription(event.target.value), placeholder: "What should the AI optimize for?" }}
            />
            <InputField
              kind="textarea"
              label="Definition of done"
              textareaProps={{
                value: definitionOfDone,
                onChange: (event) => setDefinitionOfDone(event.target.value),
                placeholder: "How will you know this task is complete?"
              }}
            />
            <InputField
              label="Due date"
              inputProps={{ type: "datetime-local", value: dueAt, onChange: (event) => setDueAt(event.target.value) }}
            />
            <InputField
              label="Attachments"
              inputProps={{
                type: "file",
                multiple: true,
                onChange: (event) => {
                  const files = Array.from(event.target.files ?? []);
                  setAttachmentNames(files.map((file) => file.name));
                }
              }}
            />
            {attachmentNames.length > 0 ? (
              <p className="text-xs text-[var(--text-secondary)]">Selected: {attachmentNames.join(", ")}</p>
            ) : (
              <p className="text-xs text-[var(--text-tertiary)]">Optional. File upload is queued for processing after task creation.</p>
            )}
            <label className="block text-sm text-[var(--text-secondary)]">
              Project
              <select className="input mt-1" value={safeProjectId} onChange={(event) => setProjectId(event.target.value)}>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm text-[var(--text-secondary)]">
              Assign to
              <select className="input mt-1" value={assigneeSelection} onChange={(event) => setAssigneeSelection(event.target.value)}>
                <option value="auto">Auto-assign (AI)</option>
                {connectedAgents.length > 0 ? (
                  <optgroup label="AI Agents">
                    {connectedAgents.map((agent) => (
                      <option key={agent.id} value={`agent:${agent.id}`}>
                        {agent.name}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
                {members.length > 0 ? (
                  <optgroup label="Members">
                    {members.map((member) => (
                      <option key={member.userId} value={`member:${member.userId}`}>
                        {member.name} ({member.role})
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </select>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-secondary)] p-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--accent-primary)]"
                checked={autoApproveRemoteAgentActions}
                onChange={(event) => setAutoApproveRemoteAgentActions(event.target.checked)}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-[var(--text-primary)]">Auto-approve remote agent requests</span>
                <span className="mt-1 block text-xs leading-5 text-[var(--text-secondary)]">
                  Approves every Hermes request for this task, including side-effectful actions. Human publish, send, and payment gates still require review.
                </span>
              </span>
            </label>
          </div>
        ) : null}

        <Button
          className="w-full"
          disabled={title.trim().length < 2 || createTaskMutation.isPending}
          onClick={async () => {
            try {
              const response = await createTaskMutation.mutateAsync({
                title: title.trim(),
                description: description.trim() || undefined,
                definitionOfDone: definitionOfDone.trim() || undefined,
                projectId: safeProjectId,
                dueAt: dueAt || undefined,
                attachmentNames,
                assigneeMode: assignee.mode,
                assigneeId: assignee.id,
                autoApproveRemoteAgentActions
              });
              const task = response.task;
              setActiveTask(task.id);
              setTitle("");
              setDescription("");
              setDefinitionOfDone("");
              setDueAt("");
              setAttachmentNames([]);
              setAssigneeSelection("auto");
              setAutoApproveRemoteAgentActions(false);
              onClose();
              navigate(task.status === "blocked" ? `/tasks/${task.id}?sheet=clarify` : `/tasks/${task.id}`);
            } catch {
              // toast emitted by mutation onError
            }
          }}
        >
          {createTaskMutation.isPending ? "Creating..." : "✦ Create Task"}
        </Button>
      </div>
    </Sheet>
  );
};
